
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Flame, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";

interface TrendingCoinCardProps {
  coin: {
    id: string;
    coinAddress?: string;
    legacyId?: string;
    name: string;
    ticker: string;
    creator: string;
    marketCap: string;
    replies: number;
    timeAgo: string;
    isHot: boolean;
    description: string;
  };
}

const TrendingCoinCard = ({ coin }: TrendingCoinCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: commentCount = 0 } = useQuery({
    queryKey: ["coin-comment-count", coin.id],
    queryFn: async () => {
      const identifiers = [
        coin.id,
        coin.coinAddress,
        coin.legacyId,
        coin.name.toLowerCase().replace(/\s+/g, "-"),
      ].filter(Boolean);
      const { count, error } = await (supabaseExtended as any)
        .from("coin_comments")
        .select("id", { count: "exact", head: true })
        .in("coin_id", [...new Set(identifiers)]);
      if (error) throw error;
      return count || 0;
    },
    staleTime: 30_000,
  });

  const handleReport = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking report
    toast({
      title: "Report Submitted",
      description: `Thank you for reporting "${coin.name}". Our moderators will review it.`,
    });
  };

  return (
    <Card 
      className="glass-card hover-scale cursor-pointer" 
      onClick={() => navigate(`/coins/${coin.id}`)}
    >
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold">{coin.name}</h3>
            <p className="text-sm text-muted-foreground">{coin.ticker}</p>
          </div>
          <div className="flex items-center gap-2">
            {coin.isHot && (
              <Badge variant="secondary" className="bg-red-500/10 text-red-500">
                <Flame className="w-3 h-3 mr-1" />
                Hot
              </Badge>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleReport}
              className="text-muted-foreground hover:text-destructive"
            >
              <Flag className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4">{coin.description}</p>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">{commentCount}</span>
          </div>
          <span className="text-muted-foreground">{coin.timeAgo}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendingCoinCard;
